import { useMemo } from "react";
import { useParams, usePathname, useSearchParams } from "next/navigation";
import { getDashboardMentionItems } from "@/lib/navigation/sidebar-items";
import { useDashboards } from "@lib/api-hooks/useDashboards";
import useSelectedStores from "@lib/hooks/use-selected-stores";
import { FeatureFlagUtils, useFeatureFlags } from "@/lib/hooks/useFeatureFlags";

export type DashboardSeedTag = {
  id: string;
  type: "dashboard";
  label: string;
};

type ResolveCurrentDashboardSeedTagInput = {
  pathname: string;
  dashboardId?: string;
  connectedChannels: string[];
  customDashboards: Array<{ id: string; title: string }>;
  allowCustomDashboards?: boolean;
};

export function resolveCurrentDashboardSeedTag({
  pathname,
  dashboardId,
  connectedChannels,
  customDashboards,
  allowCustomDashboards = true,
}: ResolveCurrentDashboardSeedTagInput): DashboardSeedTag | null {
  const builtinDashboardItems = getDashboardMentionItems(connectedChannels);
  const builtinDashboard = builtinDashboardItems.find(
    (item) => item.id === pathname
  );

  if (builtinDashboard) {
    return {
      id: builtinDashboard.id,
      type: "dashboard",
      label: builtinDashboard.label,
    };
  }

  if (!dashboardId) {
    return null;
  }

  if (!allowCustomDashboards) {
    return null;
  }

  const customDashboard = customDashboards.find(
    (dashboard) => dashboard.id === dashboardId
  );

  return {
    id: dashboardId,
    type: "dashboard",
    label: customDashboard?.title ?? dashboardId,
  };
}

export function useCurrentDashboardSeedTag(): DashboardSeedTag | null {
  const pathname = usePathname() ?? "/";
  const searchParams = useSearchParams();
  const routeDashboardId = useParams<{ id?: string }>()?.id;
  const dashboardId =
    searchParams?.get("dashboardId") ??
    (pathname.startsWith("/dashboards/") ? routeDashboardId : undefined);
  const { connectedChannels } = useSelectedStores();
  const { flags, isInitialized, error } = useFeatureFlags();
  const hasResolvedFeatureFlags = isInitialized || error !== null;
  const canUseCustomDashboards =
    hasResolvedFeatureFlags &&
    FeatureFlagUtils.isCustomInsightsEnabled(flags) &&
    Boolean(dashboardId);
  const { data: dashboards = [] } = useDashboards({
    enabled: canUseCustomDashboards,
  });

  return useMemo(
    () =>
      resolveCurrentDashboardSeedTag({
        pathname,
        dashboardId,
        connectedChannels,
        customDashboards: dashboards,
        allowCustomDashboards: canUseCustomDashboards,
      }),
    [
      canUseCustomDashboards,
      connectedChannels,
      dashboards,
      dashboardId,
      pathname,
    ]
  );
}
